import styled, { css, keyframes } from 'styled-components';
import { Icon } from '../../icon';
import {
	DropdownSize, DropdownSizes, menuItemPaddingHorizontal, menuPadding
} from '../../dropdown/DropdownSize';
import { MenuItemRoot } from '../../dropdown/Menu.styled';
import { BoxedFieldProps, boxedFieldStates } from '../../shared/boxedField';
import { transition } from '../../themes/mainTheme';

// Vertical padding is the gap chips keep from the stroke — including the second
// row, once they wrap and the box grows past its min-height.
const chipInset = 4;

export const StyledSelectInput = styled.div<{
	$size?: DropdownSize
	/** The menu is open. Flips the chevron; the stroke is `$engaged`'s business. */
	$open?: boolean
} & BoxedFieldProps>`
	font-family: ${props => props.theme.font};

	display: flex;
	flex-direction: row;
	white-space: nowrap;
	background: ${props => props.theme.form.input.background};
	// The trigger wears Dropdown's box scale, and the menu rows below take the
	// same size — the field and its options read as one control.
	${({ $size = 'compact' }) => {
		const size = DropdownSizes[$size];
		return css`
			min-height: ${size.height}px;
			padding: ${chipInset}px ${size.paddingRight}px ${chipInset}px ${size.paddingLeft}px;
			font-size: ${size.fontSize}px;
			letter-spacing: ${size.letterSpacing}px;

			// The chevron Dropdown's box draws, at the glyph size it draws it.
			${StyledChevron} {
				font-size: ${size.iconSize}px;
			}
		`;
	}}
	line-height: 1;
	position: relative;
	cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
	box-sizing: border-box;

	// Same stroke and states an Input draws. The trigger is a div, so it takes
	// $engaged to stand in for focus it cannot hold itself — but only when
	// nothing else holds it: with a search field in the menu, the caret is in a
	// real input, and that input wears the focus stroke instead.
	${boxedFieldStates}

	// Open flips the chevron, the same turn Dropdown's box gives it. The colour
	// is what an error overrides; the rotation says open either way.
	${({ $open, $hasError, theme }) => $open && css`
		${StyledChevron} {
			transform: rotate(180deg);
			${!$hasError && css`color: ${theme.form.input.accentColor};`}
		}
	`}

	::placeholder {
		color: ${props => props.theme.form.input.placeholderColor};
	}
`;

export const StyledChevron = styled(Icon)`
	flex: 0 0 auto;
	color: ${props => props.theme.text.tertiary};
	margin-left: 4px;
	align-self: center;
	transition: ${transition('color', 'transform')};
`;

export const StyledSelectionItems = styled.div`
	display: flex;
	overflow: auto;
	position: relative;
	flex: 1 auto;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
	line-height: 1;
`;

export const StyledPlaceholder = styled.span`
	color: ${props => props.theme.form.input.placeholderColor};;
	text-overflow: ellipsis;
	overflow: hidden;
	align-items: center;
	white-space: nowrap;
	align-self: center;
	flex: 1 1 auto;
`;

// Sits on the menu surface, above the scrollport rather than inside it — the
// field does not scroll, so it is not one of the rows. Bottom padding is the
// scrollport's own, which keeps the gap to the first row even at scroll top.
export const MenuSearchHeader = styled.div`
	padding: ${menuPadding}px ${menuPadding}px 0;

`;

// The select-all row and the footer live on the menu surface, not in the
// scrollport — they govern the options, so they stay put while those scroll.
// Both are separated by the inverted stroke the design draws between planes.
const surfaceDivider = 1.5;
const footerInset = 6;

export const MenuSelectAllHeader = styled.div`
	padding: ${menuPadding}px ${menuPadding}px 8px;
	border-bottom: ${surfaceDivider}px solid ${props => props.theme.border.inverted};
`;

export const MenuFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	// Two buttons are wider than a narrow menu, and clipping the last one hides
	// Apply entirely. They drop to their own line instead, which needs no
	// assumption about how wide the menu ends up.
	flex-wrap: wrap;
	padding: ${footerInset + menuItemPaddingHorizontal - 2}px;
	border-top: ${surfaceDivider}px solid ${props => props.theme.border.inverted};
	color: ${props => props.theme.text.secondary};
	font-size: 14px;
	line-height: 1;
`;

// The pill carrying the option count, right-aligned on the select-all row.
export const CountBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	min-height: 24px;
	padding: 0 6px;
	border-radius: 80px;
	background: ${props => props.theme.fill.dimmed};
	color: ${props => props.theme.text.secondary};
	// No size of its own: the count reads at the size of the label beside it.
	line-height: 1;
`;

// The gap the badge keeps from the placeholder, closed along with the badge.
const badgeGap = 6;
// The trigger's left padding is sized for the badge; a bare placeholder sat
// visibly deeper in the box than the chevron on the right. Pulling it back by
// this much lets it slide out on the same transition as the badge.
const emptyPullBack = 4;

// The badge's slot rather than the badge: nothing picked closes the column it
// sits in — the same track trick the menu opens on — so it leaves by sliding
// under the placeholder instead of blinking out from under it.
export const SelectionCountSlot = styled.div<{ $visible?: boolean }>`
	display: grid;
	// The trigger is a flex row: without this the slot stretches to the box.
	align-self: center;
	flex: 0 0 auto;
	overflow: hidden;
	// A bare fr track floors at its content's min-content — the badge's padding —
	// so the minimum is pinned to 0 and the hidden badge clips instead.
	grid-template-columns: minmax(0, ${({ $visible }) => $visible ? '1fr' : '0fr'});
	// Collapses with the track, so the placeholder closes the whole gap.
	margin-right: ${({ $visible }) => $visible ? badgeGap : -emptyPullBack}px;
	opacity: ${({ $visible }) => $visible ? 1 : 0};
	transition: ${transition('grid-template-columns', 'margin-right', 'opacity')};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

// How many are picked, on the trigger. Reads as a badge on the label rather
// than the list of labels itself, which a narrow filter cannot show anyway.
export const SelectionCount = styled(CountBadge)`
	// The slot's track has its own floor of 0, so none is needed here; the
	// padding is what a single digit measures anyway.
	min-width: 0;
	min-height: 20px;
	height: 20px;
	border-radius: 4px;
	// The trigger's own scale would outgrow that fixed height.
	font-size: 14px;
	background: ${props => props.theme.fill.brand};
	color: ${props => props.theme.text.inverted};
	font-weight: 600;
`;

// Rising means more is picked, falling means less: the numbers travel the way
// the count went, so the direction is read before the digits are.
const rollIn = (from: string) => keyframes`
	from {
		transform: translateY(${from});
		opacity: 0;
	}
`;

const rollOut = (to: string) => keyframes`
	to {
		transform: translateY(${to});
		opacity: 0;
	}
`;

// Both numbers share one cell, so the one leaving cannot push the one arriving
// sideways, and the box clips both to the line they travel along.
export const RollingDigits = styled.span`
	display: grid;
	overflow: hidden;
	// Every digit takes the same advance, so the pill keeps one width across a
	// swap rather than breathing between a 1 and an 8.
	font-variant-numeric: tabular-nums;
	// A little over the glyph, so a digit at rest is not clipped by the box
	// that has to be tight enough for the travel to read as a swap.
	line-height: 1.2;
	// Containing block for the number on its way out.
	position: relative;

	> * {
		grid-area: 1 / 1;
	}
`;

export const RollingDigit = styled.span<{ $leaving?: boolean, $rising?: boolean }>`
	// The number leaving is taken out of the flow: the cell is sized by the one
	// staying, so the pill is already the width it will settle at.
	${({ $leaving }) => $leaving && css`
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	`}
	animation: ${({ $leaving, $rising }) => $leaving
		? rollOut($rising ? '-100%' : '100%')
		: rollIn($rising ? '100%' : '-100%')}
		${props => props.theme.motion.duration.fast}
		${props => props.theme.motion.easing.default} forwards;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		// The animation is what carries the outgoing number off the cell, and
		// its end is what drops it — neither happens here, so it never shows.
		${({ $leaving }) => $leaving && css`display: none;`}
	}
`;

// A menu row carrying a checkbox instead of an icon. The box is narrower than
// the 24px frame the design draws it in, so the row reopens that gap itself —
// the same 8px every other labelled control in the form uses.
export const CheckboxMenuRow = styled(MenuItemRoot)`
	gap: 8px;
`;
